import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Flex, FlexProps, Select } from 'antd';
import { DataObjectGroupWithDescriptionsDto } from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useNavigate } from 'react-router-dom';
import { useSampleContext } from './state/SampleContext';
import translations from './SampleFilter.translations.json';
import {
  WithSampleFilterOptionLabelProps,
  WithSampleSubjectsLockIndicator,
} from './sampleSubjectLock/WithSampleSubjectsLockIndicator';
import { HeaderType } from './SampleHeader';

const { addTranslations, useTranslation } = i18nNsFactory('sample-table');
addTranslations(translations);

export type GroupMemberSelectProps = Omit<FlexProps, 'children'> & {
  view: HeaderType;
};

const SampleLabel: FC<PropsWithChildren<WithSampleFilterOptionLabelProps>> = (
  props,
) => {
  const { children, ...rest } = props;
  const { sampleView } = useSampleContext();

  if (sampleView) {
    return (
      <WithSampleSubjectsLockIndicator {...rest}>
        {children}
      </WithSampleSubjectsLockIndicator>
    );
  }

  return children;
};

export const GroupMemberSelect: FC<GroupMemberSelectProps> = (props) => {
  const { t } = useTranslation();
  const { groupOrDescription, sampleType, selectedGroupMemberId } =
    useSampleContext();
  const navigate = useNavigate();
  const isInputData = props.view === 'input-data';

  const changeGroupMember = useCallback(
    (id: number | null) => {
      const { search } = window.location;
      const searchParams = new URLSearchParams(search);
      if (id) {
        searchParams.set('groupMemberId', id.toString());
      } else {
        searchParams.delete('groupMemberId');
      }
      navigate({ search: searchParams.toString() }, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    // Select first description when no description selected and
    // changed to input data view
    if (
      sampleType === 'group' &&
      isInputData &&
      !selectedGroupMemberId &&
      groupOrDescription &&
      'descriptions' in groupOrDescription
    ) {
      const hasDescription = groupOrDescription.descriptions.some(
        (description) => description.id === selectedGroupMemberId,
      );
      if (!hasDescription) {
        changeGroupMember(groupOrDescription.descriptions?.[0]?.id ?? null);
      }
    }
  }, [
    changeGroupMember,
    groupOrDescription,
    isInputData,
    sampleType,
    selectedGroupMemberId,
  ]);

  const samplesOptions = useMemo(() => {
    if (sampleType !== 'group') {
      return [];
    }
    const options = (
      groupOrDescription as DataObjectGroupWithDescriptionsDto
    )?.descriptions.map(({ id, code, name }) => ({
      label: (
        <SampleLabel groupMemberId={id}>{`${code} - ${name}`}</SampleLabel>
      ),
      value: id,
    }));

    if (isInputData) {
      return options;
    }

    return [
      {
        label: <SampleLabel>{t('field.samples.all')}</SampleLabel>,
        value: null,
      },
      ...(options || []),
    ];
  }, [sampleType, t, groupOrDescription, isInputData]);
  let value: number | null = selectedGroupMemberId ?? null;
  if (props.view === 'input-data' && !value) {
    // Show empty selection in input data view if no valid descriptions available
    value = null;
  }

  return (
    <Flex vertical gap="small" {...props}>
      <label htmlFor="">{t('field.samples.label')}</label>
      <Select
        id="group-description-select"
        disabled={isInputData && !samplesOptions?.length}
        value={value}
        onChange={changeGroupMember}
        options={samplesOptions}
        styles={{
          popup: { root: { width: 'fit-content' } },
        }}
      />
    </Flex>
  );
};
